import { Button, CircularProgress, Divider, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { type UseFormReturn, useFieldArray, useFormState } from 'react-hook-form';
import ModalV2 from '/@/components/bayer/modal-v2';
import useUnitProvider from '/@/core/context/unit';
import { useMyFields } from '/@/hooks/fields';
import { useReports } from '/@/hooks/reports';
import { useZones } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { MuiAutocomplete } from '/@/mui/autocomplete';
import { MuiSelect } from '/@/mui/select';
import type { EVENTS as CROP_MONITORING_EVENTS } from '/@/store/tracking/events/crop-monitoring';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropType } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import {
    AVAILABLE_CROPS,
    type CropMonitoringFormType,
    countHandMadeHybrids,
    emptyHybrid,
    type FieldOption,
    getDensityRange,
    MAX_HYBRIDS,
} from '../types';
import { getInferredPreviousCrop } from '../utils';
import HybridBlock from './HybridBlock';

const NewCropMonitoringFormModal = ({
    open,
    setClose,
    goBack,
    form,
    field,
    onSubmit,
    submitting,
    onlyEdit,
    entry_source,
    onInvalidSubmit,
}: {
    open: boolean;
    setClose: () => void;
    goBack?: () => void;
    form: UseFormReturn<CropMonitoringFormType>;
    field?: Field;
    onSubmit: () => void;
    submitting: boolean;
    onlyEdit: boolean;
    entry_source: Parameters<(typeof CROP_MONITORING_EVENTS)['Add Crop Flow create began']>[0]['entry_source'];
    /** Told which fields kept the submit from going through (`planting_date`, `product`…), once per try. */
    onInvalidSubmit?: (invalidFields: string[]) => void;
}) => {
    const crop = form.watch('crop');
    const previousCrop = form.watch('previousCrop');
    const { dirtyFields } = useFormState({ control: form.control });
    const selectedFieldId = form.watch('fieldId');
    const units = useUnitProvider();
    const isCorn = crop === 'corn';

    // Two-step flow (CUL-1505): step 1 chooses the crop, step 2 collects the sowing data.
    const [step, setStep] = useState<1 | 2>(onlyEdit ? 2 : 1);

    const { fields: hybridFields, append, remove } = useFieldArray({ control: form.control, name: 'hybrids' });

    // Field search (when no field provided)
    const [fieldSearchQuery, setFieldSearchQuery] = useState('');
    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 50,
        selectOnly: 'id,name,lat,lng,crop_monitoring',
        name: fieldSearchQuery || undefined,
    });

    const fieldOptions = useMemo(() => {
        if (field || !fieldsQuery.data) return [];
        return fieldsQuery.data.results
            .filter((f: Field) => !f.activeCropMonitorings?.length)
            .map((f: Field) => ({ label: f.name, value: f.id }));
    }, [field, fieldsQuery.data]);

    const selectedField = useMemo(() => {
        if (field) return field;
        if (!selectedFieldId || !fieldsQuery.data) return null;
        return fieldsQuery.data.results.find((f: Field) => f.id === selectedFieldId);
    }, [field, selectedFieldId, fieldsQuery.data]);

    const activeField = field || selectedField;

    // Crop options from zones
    const zonesQuery = useZones({ lat: activeField?.lat, lng: activeField?.lng });
    const cropOptions = useMemo(() => {
        const cropCatalog = getCrops();
        return AVAILABLE_CROPS.filter((c) => zonesQuery.data?.some((zone) => zone.type === c))
            .map((c) => cropCatalog[c])
            .filter((c) => c !== undefined)
            .map((c) => ({ label: c.name, value: c.value }));
    }, [zonesQuery.data]);

    const previousCropsOptions = useMemo(() => {
        return [{ label: _('Sin Datos'), value: 'none' }, ...cropOptions, { label: _('Otros'), value: 'other' }];
    }, [cropOptions]);

    const plantingForPreviousCropQuery = useReports(
        { fieldId: activeField?.id ?? 0, type: 'planting' },
        { enabled: open && !!activeField && !onlyEdit && !activeField?.activeCropMonitorings?.length }
    );

    // Auto-set "Cultivo antecesor"
    useEffect(() => {
        if (onlyEdit || !activeField || dirtyFields.previousCrop) return;
        const inferred = getInferredPreviousCrop(activeField, plantingForPreviousCropQuery.data);
        if (inferred && cropOptions.some((c) => c.value === inferred)) {
            form.setValue('previousCrop', inferred, { shouldDirty: false });
        }
    }, [onlyEdit, activeField, cropOptions, plantingForPreviousCropQuery.data, form, dirtyFields.previousCrop]);

    const hasField = !!(field || selectedFieldId);
    const canContinue = !!crop && hasField;
    const maxHybridsReached = countHandMadeHybrids(hybridFields) >= MAX_HYBRIDS;
    const canAddHybrid = isCorn && !maxHybridsReached;

    const goToStep2 = () => {
        if (!crop) form.setError('crop', { type: 'required', message: _('Campo requerido') });
        if (!hasField) form.setError('fieldId', { type: 'required', message: _('Campo requerido') });
        if (canContinue) setStep(2);
    };

    const validateHybrid = (
        h: NonNullable<CropMonitoringFormType['hybrids']>[number],
        i: number,
        invalid: Set<string>
    ) => {
        const requiresTechnology = isCorn;
        let ok = true;
        if (!h.plantingDate) {
            form.setError(`hybrids.${i}.plantingDate`, { type: 'required', message: _('Campo requerido') });
            invalid.add('planting_date');
            ok = false;
        }
        if (!h.environment) {
            form.setError(`hybrids.${i}.environment`, { type: 'required', message: _('Campo requerido') });
            invalid.add('environment');
            ok = false;
        }
        if (!h.product) {
            form.setError(`hybrids.${i}.product`, { type: 'required', message: _('Campo requerido') });
            invalid.add('product');
            ok = false;
        }
        if (requiresTechnology && !h.technology) {
            form.setError(`hybrids.${i}.technology`, { type: 'required', message: _('Campo requerido') });
            invalid.add('technology');
            ok = false;
        }
        const { min: minDensity, max: maxDensity } = getDensityRange(crop);
        if (h.density && (h.density < minDensity || h.density > maxDensity)) {
            form.setError(`hybrids.${i}.density`, {
                type: 'validate',
                message: _('La densidad debe estar entre {{minDensity}} y {{maxDensity}}', {
                    minDensity: units.toDensity(minDensity, crop || 'corn', 0).localeValue,
                    maxDensity: units.toDensity(maxDensity, crop || 'corn', 0).localeValue,
                }),
            });
            invalid.add('density');
            ok = false;
        }
        return ok;
    };

    const handleSubmit = () => {
        const hybrids = form.getValues('hybrids') ?? [];
        const invalid = new Set<string>();
        const allHybridsValid = hybrids.map((h, i) => validateHybrid(h, i, invalid)).every(Boolean);

        if (!crop || !hasField || hybrids.length < 1 || !allHybridsValid) {
            if (!crop) invalid.add('crop');
            if (!hasField) invalid.add('field');
            onInvalidSubmit?.([...invalid]);
        } else {
            onSubmit();
            mpTrackEvent(onlyEdit ? 'Add Crop Flow edit completed' : 'Add Crop Flow create completed', {
                entry_source,
                crop: crop as CropType,
                planting_date: hybrids[0]?.plantingDate,
                previous_crop: previousCrop,
            });
        }
    };

    return (
        <ModalV2
            open={open}
            onClose={setClose}
            goBack={step === 2 && !onlyEdit ? () => setStep(1) : goBack}
            title={onlyEdit ? _('Editar campaña') : _('Agregar cultivo')}
            bodyProps={{ spacing: 3, alignItems: 'flex-start', textAlign: 'left' }}
            actions={
                step === 1 ? (
                    <Button
                        onClick={goToStep2}
                        variant='contained'
                        color='secondary'
                        disabled={!canContinue}
                        data-testid='field-monitoring-creation-dialog-continue-button'
                    >
                        {_('Continuar')}
                    </Button>
                ) : (
                    <Button
                        onClick={handleSubmit}
                        variant='contained'
                        color='secondary'
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : undefined}
                        data-testid='field-monitoring-creation-dialog-submit-button'
                    >
                        {onlyEdit ? _('Guardar cambios') : _('Registrar en el seguimiento')}
                    </Button>
                )
            }
            actionsProps={{ sx: { justifyContent: 'flex-end' } }}
            maxWidth='md'
        >
            {/* ---------------- Step 1: crop selection ---------------- */}
            {step === 1 && (
                <>
                    {!field && (
                        <Stack spacing={0.5} sx={{ width: '100%' }}>
                            <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                                {_('Lote')}
                            </Typography>
                            <MuiAutocomplete
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-input': { color: (t: any) => t.palette.primary.main },
                                }}
                                label={<Typography color='primary'>{_('Buscar lote')}</Typography>}
                                options={fieldOptions}
                                value={selectedFieldId}
                                onChange={(_: React.SyntheticEvent, option: FieldOption | null) => {
                                    form.setValue('fieldId', option?.value);
                                }}
                                onInputChange={(_: React.SyntheticEvent, value: string) => setFieldSearchQuery(value)}
                                getOptionLabel={(option: FieldOption) => option.label || ''}
                                getOptionValue={(option: FieldOption) => option.value}
                                loading={fieldsQuery.isValidating}
                                noOptionsText={_('No se encontraron lotes')}
                            />
                        </Stack>
                    )}

                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                            {_('Cultivo antecesor')}
                        </Typography>
                        <MuiSelect
                            options={previousCropsOptions}
                            placeholder={_('Indicá el cultivo antecesor')}
                            formControlProps={{ fullWidth: true }}
                            {...form.register('previousCrop')}
                            value={previousCrop}
                            data-testid='field-monitoring-creation-dialog-previous-crop'
                        />
                    </Stack>
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                            {_('Nuevo cultivo')}
                        </Typography>
                        <MuiSelect
                            options={cropOptions}
                            formControlProps={{ fullWidth: true }}
                            {...form.register('crop', {
                                required: true,
                                onChange: () => {
                                    // Products depend on crop: reset every hybrid to a single blank block.
                                    form.setValue('hybrids', [emptyHybrid()]);
                                },
                            })}
                            placeholder={_('Indicar cultivo')}
                            value={crop}
                            error={!!form.formState.errors.crop}
                            data-testid='field-monitoring-creation-dialog-crop'
                        />
                    </Stack>
                </>
            )}

            {/* ---------------- Step 2: sowing data ---------------- */}
            {step === 2 && activeField && (
                <>
                    <Typography variant='body2' color='primary'>
                        {_('Completá los datos de siembra para armar el seguimiento de este cultivo.')}
                    </Typography>

                    {isCorn ? (
                        <>
                            {hybridFields.map((hf, index) => (
                                <Stack key={hf.id} spacing={2} sx={{ width: '100%' }}>
                                    {index > 0 && <Divider sx={{ width: '100%' }} />}
                                    <HybridBlock
                                        form={form}
                                        index={index}
                                        activeField={activeField}
                                        crop={crop}
                                        canRemove={hybridFields.length > 1}
                                        onRemove={() => remove(index)}
                                        onlyEdit={onlyEdit}
                                    />
                                </Stack>
                            ))}

                            <Tooltip
                                arrow
                                title={
                                    maxHybridsReached
                                        ? _(
                                              'Alcanzaste el máximo de {{max}} híbridos. Editá o eliminá uno para agregar otro.',
                                              {
                                                  max: MAX_HYBRIDS,
                                              }
                                          )
                                        : ''
                                }
                            >
                                <span>
                                    <Link
                                        component='button'
                                        type='button'
                                        underline='none'
                                        color='secondary'
                                        disabled={!canAddHybrid}
                                        onClick={() => canAddHybrid && append(emptyHybrid())}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            fontWeight: 'bold',
                                            '&[disabled]': {
                                                color: (t) => t.palette.action.disabled,
                                                textDecoration: 'none',
                                                pointerEvents: 'none',
                                            },
                                        }}
                                        data-testid='field-monitoring-creation-dialog-add-hybrid'
                                    >
                                        <Typography variant='h6' color='inherit'>
                                            +
                                        </Typography>
                                        {_('agregar otro híbrido')}
                                    </Link>
                                </span>
                            </Tooltip>
                        </>
                    ) : (
                        // Other crops keep the current single set of fields, without the hybrid framing.
                        <HybridBlock
                            form={form}
                            index={0}
                            activeField={activeField}
                            crop={crop}
                            canRemove={false}
                            onRemove={() => {}}
                            onlyEdit={onlyEdit}
                            showHeader={false}
                        />
                    )}
                </>
            )}
        </ModalV2>
    );
};

export default NewCropMonitoringFormModal;
